import { type Device, DELETE_DEVICE } from '@/graphql/devices';
import { useConfirm } from '@stl/plugins/confirm';
import { useToast } from 'bootstrap-vue-next';
import { useMutation } from '@urql/vue';
import { defineStore } from 'pinia';
import { computed, nextTick, ref } from 'vue';

export const useDeviceActions = defineStore('deviceActions', () => {
  const confirm = useConfirm();
  const toast = useToast();

  const currentDevice = ref<Device | null>(null);
  const isRegisterDeviceVisible = ref(false);
  const isEditDeviceVisible = ref(false);

  const { executeMutation: executeDelete, fetching: isDeleting } = useMutation<{
    deleteRegistration: boolean;
  }>(DELETE_DEVICE);

  const isRunningAction = computed(() => {
    return isDeleting.value || isRegisterDeviceVisible.value || isEditDeviceVisible.value;
  });

  function registerDevice() {
    isRegisterDeviceVisible.value = true;
  }

  function editDevice(device: Device) {
    currentDevice.value = device;
    nextTick(() => {
      isEditDeviceVisible.value = true;
    });
  }

  function assignEnvironment(device: Device) {
    currentDevice.value = device;
    nextTick(() => {
      isEditDeviceVisible.value = true;
    });
  }

  function deleteDevice(device: Device) {
    confirm
      .show(`Are you sure you want to delete device ${device.name || device.serialNumber}?`, {
        title: 'Delete Device?',
        yesText: 'Delete',
        yesVariant: 'danger',
        noText: 'Cancel'
      })
      .then((dialog) => {
        if (dialog.result) {
          executeDelete({
            registrationId: device.id
          }).then((result) => {
            if (result.error || !result.data?.deleteRegistration) {
              const errMsg = result.error?.message || 'Delete failed for unknown reason';
              toast.show?.({ props: { variant: 'danger', body: errMsg } });
              return;
            }
            toast.show?.({
              props: { variant: 'success', body: `Successfully deleted ${device.name || device.serialNumber}` }
            });
          });
        }
        dialog.close(true);
      });
  }

  return {
    currentDevice,
    assignEnvironment,
    isEditDeviceVisible,
    editDevice,
    isRegisterDeviceVisible,
    registerDevice,
    deleteDevice,
    isRunningAction
  };
});
